// Republic Atlas - Authentication Store (Zustand + Firebase)

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  type User as FirebaseUser,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import type { User, UserRole } from '@/types';
import type { UXMode, SubscriptionTier, ProFeatures } from '@/types/ux';
import { TIER_FEATURES } from '@/types/ux';

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// Initialize Firebase (only if config is available)
let auth: ReturnType<typeof getAuth> | null = null;
let googleProvider: GoogleAuthProvider | null = null;

if (firebaseConfig.apiKey) {
  const app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  googleProvider = new GoogleAuthProvider();
  
  // Set persistence to LOCAL - user stays logged in after browser close/refresh
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('[Auth] Firebase persistence set to LOCAL');
    })
    .catch((error) => {
      console.error('[Auth] Failed to set persistence:', error);
    });
}

// Admin emails that get developer role automatically
const ADMIN_EMAILS = [
  'duketopceo@gmail.com',
];

// Convert Firebase user to our User type
const firebaseUserToUser = (firebaseUser: FirebaseUser, customClaims?: Record<string, unknown>): User => {
  // Check if user email is in admin list
  const email = firebaseUser.email || '';
  const isAdminEmail = ADMIN_EMAILS.includes(email.toLowerCase());
  
  // Get role from custom claims, or fall back to admin for admin emails
  let role: UserRole = 'viewer';
  if (customClaims?.role && typeof customClaims.role === 'string') {
    role = customClaims.role as UserRole;
  } else if (customClaims?.admin === true || isAdminEmail) {
    role = 'admin';
  } else if (customClaims?.developer === true) {
    role = 'editor'; // developer maps to editor role
  }

  // Check subscription status from claims
  const subscriptionStatus = customClaims?.premium === true ? 'active' : 'free';

  return {
    id: firebaseUser.uid,
    firebaseUid: firebaseUser.uid,
    email,
    displayName: firebaseUser.displayName || null,
    profileImageUrl: firebaseUser.photoURL || null,
    role,
    subscriptionStatus,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  
  // UX Mode State
  uxMode: UXMode;
  
  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => () => void;
  
  // UX Mode Actions
  setUXMode: (mode: UXMode) => void;
  toggleUXMode: () => void;

  // Computed helpers
  hasRole: (role: UserRole) => boolean;
  isAdmin: () => boolean;
  isPremium: () => boolean;
  
  // UX Mode helpers
  isProUser: () => boolean;
  canAccessProMode: () => boolean;
  getSubscriptionTier: () => SubscriptionTier;
  getFeatures: () => ProFeatures;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isInitialized: false,
      
      // UX Mode - defaults to standard, persisted
      uxMode: 'standard' as UXMode,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      // Email/Password login
      login: async (email: string, password: string) => {
        // Check for dev mode first
        if (typeof window !== 'undefined') {
          const { isDevModeEnabled, getDevUser } = await import('@/utils/devMode');
          if (isDevModeEnabled()) {
            const devUser = getDevUser(email);
            if (devUser) {
              // Dev mode login - bypass Firebase
              set({ isLoading: true, error: null });
              const user: User = {
                id: devUser.id,
                firebaseUid: devUser.id,
                email: devUser.email,
                displayName: devUser.displayName,
                profileImageUrl: null,
                role: devUser.role,
                subscriptionStatus: devUser.tier === 'free' ? 'free' : 'active',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              set({ user, isAuthenticated: true, isLoading: false });
              return;
            }
          }
        }

        if (!auth) {
          set({ error: 'Firebase not configured. Please set up environment variables.' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const result = await signInWithEmailAndPassword(auth, email, password);
          // Get custom claims from ID token
          const idTokenResult = await result.user.getIdTokenResult();
          const customClaims = idTokenResult.claims as Record<string, unknown>;
          const user = firebaseUserToUser(result.user, customClaims);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (err: unknown) {
          const error = err as { code?: string; message?: string };
          let message = 'Login failed. Please try again.';
          if (error.code === 'auth/user-not-found') {
            message = 'No account found with this email.';
          } else if (error.code === 'auth/wrong-password') {
            message = 'Incorrect password.';
          } else if (error.code === 'auth/invalid-email') {
            message = 'Invalid email address.';
          } else if (error.code === 'auth/too-many-requests') {
            message = 'Too many attempts. Please try again later.';
          }
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Email/Password sign up
      signUp: async (email: string, password: string) => {
        if (!auth) {
          set({ error: 'Firebase not configured. Please set up environment variables.' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const result = await createUserWithEmailAndPassword(auth, email, password);
          // Get custom claims from ID token
          const idTokenResult = await result.user.getIdTokenResult();
          const customClaims = idTokenResult.claims as Record<string, unknown>;
          const user = firebaseUserToUser(result.user, customClaims);
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (err: unknown) {
          const error = err as { code?: string; message?: string };
          let message = 'Sign up failed. Please try again.';
          if (error.code === 'auth/email-already-in-use') {
            message = 'An account with this email already exists.';
          } else if (error.code === 'auth/invalid-email') {
            message = 'Invalid email address.';
          } else if (error.code === 'auth/weak-password') {
            message = 'Password should be at least 6 characters.';
          }
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Google sign in (uses redirect to avoid COOP issues)
      loginWithGoogle: async () => {
        if (!auth || !googleProvider) {
          set({ error: 'Firebase not configured. Please set up environment variables.' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          // Use redirect instead of popup to avoid COOP blocking issues
          await signInWithRedirect(auth, googleProvider);
          // Note: The page will redirect to Google, then back to our app
          // The result is handled in initialize() via getRedirectResult()
        } catch (err: unknown) {
          const error = err as { code?: string; message?: string };
          const message = error.message || 'Google sign in failed. Please try again.';
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Logout
      logout: async () => {
        if (auth) {
          await signOut(auth);
        }
        localStorage.removeItem('auth_token');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      // Initialize auth state listener
      initialize: () => {
        if (!auth) {
          set({ isInitialized: true, isLoading: false });
          return () => {};
        }

        set({ isLoading: true });

        // Check for redirect result (handles return from Google sign-in)
        getRedirectResult(auth)
          .then(async (result) => {
            if (result?.user) {
              console.log('[Auth] Redirect sign-in successful');
              const idTokenResult = await result.user.getIdTokenResult();
              const customClaims = idTokenResult.claims as Record<string, unknown>;
              const user = firebaseUserToUser(result.user, customClaims);
              set({ user, isAuthenticated: true, isLoading: false, isInitialized: true });
            } else {
              // No redirect result - this is normal if user didn't come from redirect
              set({ isLoading: false });
            }
          })
          .catch((err) => {
            const error = err as { code?: string; message?: string };
            console.error('[Auth] Redirect result error:', error);
            // Don't show error for normal cases (no redirect, user cancelled)
            if (error.code && !['auth/popup-closed-by-user'].includes(error.code)) {
              set({ error: error.message || 'Sign in failed', isLoading: false });
            } else {
              set({ isLoading: false });
            }
          });

        // Set up auth state listener for all auth changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            // Get custom claims from ID token
            const idTokenResult = await firebaseUser.getIdTokenResult();
            const customClaims = idTokenResult.claims as Record<string, unknown>;
            const user = firebaseUserToUser(firebaseUser, customClaims);
            set({ user, isAuthenticated: true, isLoading: false, isInitialized: true });
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false, isInitialized: true });
          }
        });

        return unsubscribe;
      },

      // Computed helpers
      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;

        const roleHierarchy: Record<UserRole, number> = {
          viewer: 0,
          premium_user: 1,
          editor: 2,
          admin: 3,
        };

        return roleHierarchy[user.role] >= roleHierarchy[role];
      },

      isAdmin: () => get().user?.role === 'admin',

      isPremium: () => {
        const { user } = get();
        return (
          user?.subscriptionStatus === 'active' ||
          user?.role === 'editor' ||
          user?.role === 'admin'
        );
      },
      
      // UX Mode Actions
      setUXMode: (mode: UXMode) => {
        const canAccess = get().canAccessProMode();
        // Only allow pro mode if user has access
        if (mode === 'pro' && !canAccess) {
          console.warn('[Auth] User does not have Pro access');
          return;
        }
        set({ uxMode: mode });
      },
      
      toggleUXMode: () => {
        const { uxMode, canAccessProMode } = get();
        if (!canAccessProMode()) {
          console.warn('[Auth] User does not have Pro access');
          return;
        }
        set({ uxMode: uxMode === 'standard' ? 'pro' : 'standard' });
      },
      
      // UX Mode Helpers
      isProUser: () => {
        const { user } = get();
        if (!user) return false;
        // Pro users: premium subscribers, editors, or admins
        return (
          user.subscriptionStatus === 'active' ||
          user.role === 'premium_user' ||
          user.role === 'editor' ||
          user.role === 'admin'
        );
      },
      
      canAccessProMode: () => {
        // Same as isProUser - determines if user can switch to Pro view
        return get().isProUser();
      },
      
      getSubscriptionTier: (): SubscriptionTier => {
        // Check for dev mode tier override first
        if (typeof window !== 'undefined') {
          const devTier = localStorage.getItem('dev_tier_override');
          if (devTier && ['free', 'premium', 'pro'].includes(devTier)) {
            return devTier as SubscriptionTier;
          }
        }
        
        // Check user's actual subscription
        const user = get().user;
        if (!user) return 'free';
        
        // Check custom claims or subscription status
        if (user.role === 'admin') return 'pro';
        if (user.subscriptionStatus === 'active') {
          // TODO: Get actual tier from subscription service
          // For now, default to premium for active subscriptions
          return 'premium';
        }
        return 'free';
      },
      
      getFeatures: (): ProFeatures => {
        const tier = get().getSubscriptionTier();
        return TIER_FEATURES[tier];
      },
    }),
    {
      name: 'republic-atlas-auth',
      partialize: (state) => ({ user: state.user, uxMode: state.uxMode }),
    }
  )
);

export default useAuthStore;
